import { Typography } from '@mui/material';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormAutocomplete, FormSelect } from 'src/components/Forms';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';

type BrandSegment = {
  id?: string | number | null;
  brandId: string | number;
  name?: string | null;
};

export interface CopyConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (sourceBrandId: string, targetBrandIds: string[]) => Promise<void>;
  segments: BrandSegment[];
  loading: boolean;
}

enum CopyConfigFormField {
  Source = 'source',
  Targets = 'targets',
}

interface CopyConfigFormValues {
  [CopyConfigFormField.Source]: string;
  [CopyConfigFormField.Targets]: Option[];
}

const defaultValues: CopyConfigFormValues = {
  [CopyConfigFormField.Source]: '',
  [CopyConfigFormField.Targets]: [],
};

const toOption = ({ brandId, name }: BrandSegment): Option => {
  const id = String(brandId);
  return { id, value: id, label: name || 'Untitled segment' };
};

export const CopyConfigModal = ({
  open,
  onClose,
  onSubmit,
  segments,
  loading,
}: CopyConfigModalProps) => {
  const formMethods = useForm<CopyConfigFormValues>({ defaultValues, mode: 'onChange' });
  const { control, formState, getValues, handleSubmit, reset, setValue } = formMethods;
  const sourceBrandId = useWatch({ control, name: CopyConfigFormField.Source });
  const targets = useWatch({ control, name: CopyConfigFormField.Targets });

  const segmentOptions = useMemo(() => segments.map(toOption), [segments]);
  const sourceOptions = useMemo(
    () => segmentOptions.filter(({ id }) => !targets.some((target) => target.id === id)),
    [segmentOptions, targets],
  );
  const targetOptions = useMemo(
    () => segmentOptions.filter(({ id }) => id !== sourceBrandId),
    [segmentOptions, sourceBrandId],
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSourceChange = (sourceId: Option['value']) => {
    setValue(
      CopyConfigFormField.Targets,
      getValues(CopyConfigFormField.Targets).filter(({ id }) => id !== sourceId),
      { shouldValidate: true },
    );
  };

  const submit = handleSubmit(async (values) => {
    await onSubmit(
      values[CopyConfigFormField.Source],
      values[CopyConfigFormField.Targets].map(({ id }) => id),
    );
    handleClose();
  });

  return (
    <ModalContainer
      open={open}
      size="medium"
      title="Copy configuration"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'copy-config-cancel' },
        {
          label: 'Copy configuration',
          kind: 'primary',
          onClick: () => void submit(),
          disabled: !formState.isValid,
          loading,
          testId: 'copy-config-submit',
        },
      ]}
      onClose={handleClose}
      data-testid="copy-config-modal"
    >
      <GenericForm<CopyConfigFormValues> formMethods={formMethods}>
        <Typography variant="body2" color="text.secondary">
          Copy the activation flow configuration (plans, pricing, features, and options) from
          one client segment to others. Each target segment&apos;s existing configuration will
          be overwritten.
        </Typography>
        <FormSelect
          type="select"
          name={CopyConfigFormField.Source}
          label="Source segment"
          required
          options={sourceOptions}
          onChange={handleSourceChange}
          data-testid="copy-config-source"
        />
        <FormAutocomplete
          type="autocomplete"
          multiple
          name={CopyConfigFormField.Targets}
          label="Target segments"
          required
          options={targetOptions}
          data-testid="copy-config-target"
        />
      </GenericForm>
    </ModalContainer>
  );
};
